<template>
  <q-card flat bordered>
    <q-dialog v-model="displayUnitPopup" transition-duration="100" @before-hide="closeUnitPopup">
      <q-card v-if="clickedCell" style="max-width: 1600px">
        <q-card-section>
          <div class="row justify-between">
            <div>
              <div>
                {{ clickedCell.prop.building_name || clickedCell.prop.street_address }}
              </div>
              <div class="row q-gutter-x-sm items-center">
                <div class="text-h5 text-bold">
                  {{ clickedCell.label }} -
                  {{
                    clickedCell.field.formattedValue === undefined
                      ? clickedCell.field.value
                      : clickedCell.field.formattedValue
                  }}
                  {{ clickedCell.field.unit ? clickedCell.field.unit : '' }}
                </div>
              </div>
            </div>
            <q-icon size="sm" name="close" class="cursor-pointer" @click="closeUnitPopup" />
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <property-compare-unit-details
            :expanded-unit-history="expandedUnitHistory"
            :filtered-properties="[{ prop: clickedCell.prop, units: clickedCell.units }]"
            hide-filter
          />
        </q-card-section>
      </q-card>
    </q-dialog>
    <div id="intercom-header" class="q-pa-md flex justify-between q-gutter-y-sm">
      <div>
        <div class="text-h6">Rents by Unit Type</div>
        <div style="font-size: 12px">
          Analyze rent and availability trends among your market with unit-type level data. Click on
          a value to see the individual units used in that calculation.
        </div>
      </div>

      <div
        id="intercom-rents-by-unit-type-actions"
        class="flex q-gutter-sm"
        data-html2canvas-ignore
      >
        <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
          <q-icon name="download" class="q-mr-sm" size="medium" />Download
        </q-btn>
        <q-select
          v-model="metric"
          transition-duration="100"
          :options="metricOptions"
          label="Trend"
          dense
          outlined
          style="width: 180px"
          @update:model-value="(val) => $tracker.changedFilter('metric', 'unit_types', val)"
        />
      </div>
    </div>
    <q-table
      id="intercom-rents-by-unit-type-content"
      :rows="rows"
      :columns="columns"
      :rows-per-page-options="[0]"
      hide-bottom
      dense
      bordered
      row-key="id"
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #header-cell="props">
        <q-th :props="props" @click="sortingChanged(props.col.name)">
          {{ props.col.label }}
          <q-tooltip v-if="props.col.description" max-width="250px">
            <b>{{ props.col.label }}</b>
            <br />
            {{ props.col.description }}
          </q-tooltip>
        </q-th>
      </template>
      <template #body="props">
        <q-tr
          :props="props"
          :class="{
            'text-bold bg-deep-purple-light text-primary subject':
              subjectDetail && props.row.id === subjectDetail.id
          }"
        >
          <q-td
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
            :class="{ 'no-space': col.name === 'label' }"
          >
            <div v-if="col.name === 'Trend' && props.row.trend" class="row justify-center">
              <chart-sparkline
                :values="props.row.trend"
                :label="`${metric.value === 'effective_rent' ? 'Avg Effective Rent PSF' : 'Avg Asking Rent PSF'}`"
              />
            </div>
            <template v-else-if="col.field === Column.CONCESSION_PERCENT">
              <show-concessions-popup-table-cell :property="props.row.prop" :date-range="dateRange">
                {{ col.value }}
              </show-concessions-popup-table-cell>
            </template>
            <span
              v-else
              :class="[
                col.name === 'label' ? 'constrained-cell' : '',
                props.row[col.field]?.units?.length ? 'clickable-table-cell' : ''
              ]"
              @click="
                col.name !== 'label' &&
                openUnitPopup(
                  col.label,
                  props.row.prop,
                  {
                    value: props.row[col.field].value,
                    formattedValue: getColumnFormattedValue(col.field, props.row),
                    unit: col.unit
                  },
                  props.row[col.field].units
                )
              "
            >
              <q-btn
                v-if="col.name === 'label' && props.row.units?.length"
                size="xs"
                class="q-mr-xs"
                flat
                dense
                :icon="props.expand ? 'expand_less' : 'expand_more'"
                @click.stop="togglePropertyRow(props)"
              />
              <span v-else-if="col.name === 'label'" class="q-mr-lg"> </span>
              {{ col.value }}
            </span>
          </q-td>
        </q-tr>
        <template v-if="props.expand">
          <template v-for="row in getUnitTypeRows(props.row)" :key="row.label">
            <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
              <q-td v-for="col in props.cols" :key="col.field" class="text-center">
                <div v-if="col.name === 'label'" class="q-pl-md row justify-start">
                  <q-btn
                    size="xs"
                    class="q-mr-sm"
                    flat
                    dense
                    :icon="
                      expandedBedRows.get(row.prop.id) === props.row.prop.id
                        ? 'expand_less'
                        : 'expand_more'
                    "
                    @click.stop="toggleBed(row, props.row)"
                  />
                  <div :class="`hd-${parseInt(row.label.split(' ')[0])}-bed-bg hd-badge`">
                    {{ row.label }}
                  </div>
                </div>
                <div v-else-if="col.name === 'Trend'" class="row justify-center">
                  <chart-sparkline
                    :values="row.trend"
                    :label="`${metric.value === 'effective_rent' ? 'Avg Effective Rent PSF' : 'Avg Asking Rent PSF'}`"
                  />
                </div>
                <template v-else-if="col.field === Column.CONCESSION_PERCENT">
                  <show-concessions-popup-table-cell
                    :property="props.row.prop"
                    :date-range="dateRange"
                  >
                    {{ getColumnFormattedValue(col.field, row) }}
                  </show-concessions-popup-table-cell>
                </template>
                <span
                  v-else
                  :class="row[col.field]?.units?.length ? 'clickable-table-cell' : ''"
                  @click="
                    openUnitPopup(
                      col.label,
                      row.prop,
                      {
                        value: row[col.field].value,
                        formattedValue: getColumnFormattedValue(col.field, row),
                        unit: col.unit
                      },
                      row[col.field].units
                    )
                  "
                >
                  {{ getColumnFormattedValue(col.field, row) }}
                </span>
              </q-td>
            </q-tr>
            <template v-if="expandedBedRows.get(row.id) === props.row.id">
              <q-tr
                v-for="floorplan in getFloorplanRows(row)"
                :key="floorplan.name"
                :class="[$q.dark.isActive ? 'bg-grey-8' : 'bg-grey-3']"
              >
                <q-td v-for="col in props.cols" :key="col.field" class="text-center">
                  <div
                    v-if="col.name === 'label'"
                    class="q-pl-xxl row justify-start relative-position"
                  >
                    <div
                      class="floorplan-line"
                      :class="`hd-${row.label.split('/')[0]}-bed-bg`"
                    ></div>
                    <div class="text-caption q-pl-xl">
                      {{ floorplan.name }}
                    </div>
                  </div>
                  <div v-else-if="col.name === 'Trend'" class="row justify-center">
                    <chart-sparkline
                      :values="floorplan.trend"
                      :label="`${metric.value === 'effective_rent' ? 'Avg Effective Rent PSF' : 'Avg Asking Rent PSF'}`"
                    />
                  </div>
                  <template v-else-if="col.field === Column.CONCESSION_PERCENT">
                    <show-concessions-popup-table-cell
                      :property="props.row.prop"
                      :date-range="dateRange"
                    >
                      {{ getColumnFormattedValue(col.field, floorplan) }}
                    </show-concessions-popup-table-cell>
                  </template>
                  <span
                    v-else
                    :class="floorplan[col.field]?.units?.length ? 'clickable-table-cell' : ''"
                    @click="
                      openUnitPopup(
                        col.label,
                        floorplan.prop,
                        {
                          value: floorplan[col.field].value,
                          formattedValue: getColumnFormattedValue(col.field, floorplan),
                          unit: col.unit
                        },
                        floorplan[col.field].units
                      )
                    "
                  >
                    {{ getColumnFormattedValue(col.field, floorplan) }}
                  </span>
                </q-td>
              </q-tr>
            </template>
          </template>
        </template>
        <q-tr
          v-if="props.rowIndex === rows.length - 1"
          class="text-bold text-center title-row"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <q-td class="constrained-cell text-left">
            <div>
              Comp Average
              <q-icon v-if="subjectDetail" name="info_outline">
                <q-tooltip>
                  Computed with underlying units (Does not include
                  {{ subjectDetail.building_name || subjectDetail.street_address }})
                </q-tooltip>
              </q-icon>
            </div>
          </q-td>
          <q-td v-for="(val, i) in footerRow" :key="i" class="text-center">
            {{ val }}
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </q-card>
</template>

<script lang="ts">
import { type PropType } from 'vue'
import { format, min, parseISO } from 'date-fns'

interface UnitData {
  unit: Availability
  lastHistory: Price
  period: AvailabilityPeriod
}

interface ColumnCellData {
  units: UnitData[]
  value: string | number | null
}

import { exportFile } from 'quasar'

import {
  type Availability,
  type AvailabilityPeriod,
  avgPsf,
  Column,
  daysOnMarket,
  getTrend,
  type Price,
  type PropertyDetailsResponse
} from 'common'
import {
  avg,
  filterColumns,
  formatMoney,
  formatNumber,
  formatPercent,
  unitTypeColor,
  type ColumnData,
  sortColumns
} from '@/utils'
import ChartSparkline from '@/components/Charts/ChartSparkline.vue'
import ShowConcessionsPopupTableCell from '@/components/PropertyReports/ShowConcessionsPopupTableCell.vue'
import PropertyCompareUnitDetails from '@/components/PropertyReports/PropertyCompareUnitDetails.vue'

type MetricOption = 'asking_rent' | 'effective_rent'

const safeMin = (vals: (number | null)[]) => {
  const cleanVals = vals.filter((_) => _) as number[]
  return cleanVals.length ? Math.min(...cleanVals) : null
}
const safeMax = (vals: (number | null)[]) => {
  const cleanVals = vals.filter((_) => _) as number[]
  return cleanVals.length ? Math.max(...cleanVals) : null
}

export default {
  name: 'PropertyCompareUnitTypeTable',
  components: { PropertyCompareUnitDetails, ShowConcessionsPopupTableCell, ChartSparkline },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: UnitData[]
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    visibleColumns: {
      type: Array as PropType<Column[]>,
      required: true
    },
    toggleableColumns: {
      type: Object as PropType<ColumnData>,
      required: true
    }
  },
  data() {
    return {
      unitTypeColor,
      formatMoney,
      formatNumber,
      Column,
      formatPercent,
      expandedBedRows: new Map(),
      displayUnitPopup: false,
      clickedCell: null as null | {
        label: string
        prop: PropertyDetailsResponse
        field: {
          value: string | number | null
          formattedValue?: string
          unit?: string
        }
        units: UnitData[]
      },
      metric: { label: 'Asking Rent', value: 'asking_rent' } as {
        label: string
        value: MetricOption
      },
      metricOptions: [
        { label: 'Asking Rent', value: 'asking_rent' },
        { label: 'Effective Rent', value: 'effective_rent' }
      ] as { label: string; value: MetricOption }[]
    }
  },
  computed: {
    footerRow(): (string | null)[] {
      const initialFooterData = {
        number_leased: [] as number[],
        currently_available: [] as number[],
        days_on_market: [] as (number | null)[],
        market_rent: [] as (number | null)[],
        min_market_rent: [] as (number | null)[],
        max_market_rent: [] as (number | null)[],
        effective_rent: [] as (number | null)[],
        sqft: [] as (number | null)[],
        min_sqft: [] as (number | null)[],
        max_sqft: [] as (number | null)[],
        askPsf: [] as { price: number; sqft: number }[],
        effPsf: [] as { price: number; sqft: number }[],
        concession_percent: [] as number[],
        year_built: [] as (number | null)[]
      }

      const footerData = this.filteredProperties.reduce((acc, { prop, units }) => {
        if (prop.id === this.subjectDetail?.id) return acc

        acc.year_built.push(prop.year_built)
        const maxDate = this.dateRange.to
          ? min([parseISO(prop.created_on), this.dateRange.to])
          : parseISO(prop.created_on)
        const maxDateStr = format(maxDate, 'yyyy-MM-dd')

        let leasedCount = 0
        for (const { unit, lastHistory, period } of units) {
          if (period.exit_market && period.exit_market <= maxDateStr) {
            leasedCount += 1
          }

          const days = daysOnMarket(period, this.dateRange.to)
          acc.days_on_market.push(days)

          const askPrice = lastHistory.price ?? lastHistory.min_price
          acc.market_rent.push(askPrice)

          const effPrice = lastHistory.effective_price || lastHistory.min_effective_price
          acc.effective_rent.push(effPrice)

          const sqft = unit.sqft || unit.min_sqft
          acc.sqft.push(sqft)

          if (lastHistory.price && lastHistory.effective_price) {
            acc.concession_percent.push(
              (lastHistory.price - lastHistory.effective_price) / lastHistory.price
            )
          }

          if (sqft && askPrice) {
            acc.askPsf.push({ price: askPrice, sqft })
          }
          if (sqft && effPrice) {
            acc.effPsf.push({ price: effPrice, sqft })
          }
        }

        const allPropPrices = units
          .flatMap(({ lastHistory }) => lastHistory.price ?? lastHistory.min_price)
          .filter((_) => _) as number[]

        if (allPropPrices.length) {
          acc.min_market_rent.push(Math.min(...allPropPrices))
          acc.max_market_rent.push(Math.max(...allPropPrices))
        }

        const allPropSfs = units
          .flatMap(({ unit }) => unit.sqft || unit.min_sqft)
          .filter((_) => _) as number[]
        if (allPropSfs.length) {
          acc.min_sqft.push(Math.min(...allPropSfs))
          acc.max_sqft.push(Math.max(...allPropSfs))
        }

        acc.number_leased.push(leasedCount)

        return acc
      }, initialFooterData)

      const avgValues = {
        number_leased: avg(footerData.number_leased),
        currently_available: avg(footerData.currently_available),
        days_on_market: avg(footerData.days_on_market),
        min_sqft: avg(footerData.min_sqft),
        sqft: avg(footerData.sqft),
        max_sqft: avg(footerData.max_sqft),
        min_market_rent: avg(footerData.min_market_rent),
        market_rent: avg(footerData.market_rent),
        max_market_rent: avg(footerData.max_market_rent),
        rent_psf: avgPsf(footerData.askPsf),
        effective_rent: avg(footerData.effective_rent),
        effective_rent_psf: avgPsf(footerData.effPsf),
        concession_percent: avg(footerData.concession_percent),
        year_built: avg(footerData.year_built)
      }

      const footerColumnValues = [
        {
          field: Column.NUMBER_LEASED,
          value: formatNumber(avgValues.number_leased)
        },
        {
          field: Column.CURRENTLY_AVAILABLE,
          value: formatNumber(avgValues.currently_available)
        },
        {
          field: Column.DAYS_ON_MARKET,
          value: formatNumber(avgValues.days_on_market)
        },
        {
          field: Column.MIN_SQFT,
          value: formatNumber(avgValues.min_sqft)
        },
        {
          field: Column.SQFT,
          value: formatNumber(avgValues.sqft)
        },
        {
          field: Column.MAX_SQFT,
          value: formatNumber(avgValues.max_sqft)
        },
        {
          field: Column.MIN_MARKET_RENT,
          value: formatMoney(avgValues.min_market_rent, 0)
        },
        {
          field: Column.MARKET_RENT,
          value: formatMoney(avgValues.market_rent, 0)
        },
        {
          field: Column.MAX_MARKET_RENT,
          value: formatMoney(avgValues.max_market_rent, 0)
        },
        {
          field: Column.RENT_PSF,
          value: formatMoney(avgValues.rent_psf, 2)
        },
        {
          field: Column.EFFECTIVE_RENT,
          value: formatMoney(avgValues.effective_rent, 0)
        },
        {
          field: Column.EFFECTIVE_RENT_PSF,
          value: formatMoney(avgValues.effective_rent_psf, 2)
        },
        {
          field: Column.CONCESSION_PERCENT,
          value: formatPercent(avgValues.concession_percent)
        },
        {
          field: Column.YEAR_BUILT,
          value: avgValues.year_built ? Math.round(avgValues.year_built).toString() : ''
        },
        {
          field: Column.TREND,
          value: ''
        }
      ]

      return footerColumnValues
        .filter((column) => {
          return filterColumns(column.field, this.toggleableColumns, this.visibleColumns)
        })
        .sort((columnA, columnB) => {
          return sortColumns(columnA.field, columnB.field, this.columnOrder)
        })
        .map((_) => _.value) as (string | null)[]
    },
    columnOrder(): Column[] {
      return [Column.LABEL, ...this.visibleColumns]
    },
    rows() {
      return this.filteredProperties.map(({ prop, units }) => {
        return {
          id: prop.id,
          prop,
          units,
          ...this.getPropertyRow(prop, prop.building_name || prop.street_address, units)
        }
      })
    },
    columns() {
      return [
        { name: 'label', label: '', field: Column.LABEL, align: 'left' },
        {
          name: '# Leased',
          label: '# Leased',
          field: Column.NUMBER_LEASED,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.NUMBER_LEASED].description,
          unit: 'units'
        },
        {
          name: '# Active',
          label: '# Active',
          field: Column.CURRENTLY_AVAILABLE,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.CURRENTLY_AVAILABLE].description,
          unit: 'units'
        },
        {
          name: 'Days on Mkt',
          label: 'Days on Mkt',
          field: Column.DAYS_ON_MARKET,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.DAYS_ON_MARKET].description,
          unit: 'days'
        },
        {
          name: 'Min SF',
          label: 'Min SF',
          field: Column.MIN_SQFT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.MIN_SQFT].description,
          unit: 'ft²'
        },
        {
          name: 'Avg SF',
          label: 'Avg SF',
          field: Column.SQFT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.SQFT].description,
          unit: 'ft²'
        },
        {
          name: 'Max SF',
          label: 'Max SF',
          field: Column.MAX_SQFT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.MAX_SQFT].description,
          unit: 'ft²'
        },
        {
          name: 'Min Rent',
          label: 'Min Rent',
          field: Column.MIN_MARKET_RENT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => (x.value ? formatMoney(x.value, 0) : ''),
          description: this.toggleableColumns[Column.MIN_MARKET_RENT].description
        },
        {
          name: 'Avg Rent',
          label: 'Avg Rent',
          field: Column.MARKET_RENT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) =>
            x.value ? formatMoney(Math.round(x.value)) : '',
          description: this.toggleableColumns[Column.MARKET_RENT].description
        },
        {
          name: 'Max Rent',
          label: 'Max Rent',
          field: Column.MAX_MARKET_RENT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => (x.value ? formatMoney(x.value, 0) : ''),
          description: this.toggleableColumns[Column.MAX_MARKET_RENT].description
        },
        {
          name: 'PSF',
          label: 'Avg PSF',
          field: Column.RENT_PSF,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatMoney(x.value, 2),
          description: this.toggleableColumns[Column.RENT_PSF].description,
          unit: '/ft²'
        },
        {
          name: 'NER',
          label: 'NER',
          field: Column.EFFECTIVE_RENT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) =>
            x.value ? formatMoney(Math.round(x.value)) : '',
          description: this.toggleableColumns[Column.EFFECTIVE_RENT].description
        },
        {
          name: 'EffPSF',
          label: 'NER PSF',
          field: Column.EFFECTIVE_RENT_PSF,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatMoney(x.value, 2),
          description: this.toggleableColumns[Column.EFFECTIVE_RENT_PSF].description,
          unit: '/ft²'
        },
        {
          name: 'Concession %',
          label: 'Concession %',
          field: Column.CONCESSION_PERCENT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatPercent(x.value),
          description: this.toggleableColumns[Column.CONCESSION_PERCENT].description
        },
        {
          name: 'Year Built',
          label: 'Year Built',
          field: Column.YEAR_BUILT,
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          sort: this.sortColumn,
          format: (x: { value: number | null }) => x.value,
          description: this.toggleableColumns[Column.YEAR_BUILT].description
        },
        {
          name: 'Trend',
          field: Column.TREND,
          label: 'Trend',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          description: this.toggleableColumns[Column.TREND].description
        }
      ]
        .filter((column) => {
          if (column) {
            return filterColumns(column.field, this.toggleableColumns, this.visibleColumns)
          }
          return false
        })
        .sort((columnA, columnB) => {
          return sortColumns(columnA.field, columnB.field, this.columnOrder)
        })
    }
  },
  methods: {
    closeUnitPopup() {
      this.clickedCell = null
      this.displayUnitPopup = false
    },
    getUnitAttr<T>(
      preFilteredUnits: UnitData[],
      computeFnc: (payload: UnitData) => T | T[],
      cleanFnc: (x: T[]) => string | number | null
    ): ColumnCellData {
      const result: T[] = []
      const contributingUnits: typeof preFilteredUnits = []

      for (const unit of preFilteredUnits) {
        const computed = computeFnc(unit)
        if (Array.isArray(computed)) {
          let hasContribution = false
          for (const item of computed) {
            if (item != null && item !== false) {
              result.push(item)
              hasContribution = true
            }
          }
          if (hasContribution) {
            contributingUnits.push(unit)
          }
        } else if (computed != null) {
          result.push(computed)
          contributingUnits.push(unit)
        }
      }

      return {
        value: cleanFnc(result),
        units: contributingUnits
      }
    },
    exportCSV() {
      const exportDecimal = (num: number | string | null) => {
        return typeof num === 'number' ? Math.round(num * 100) / 100 : null
      }

      const data = this.rows
        .flatMap((row) => {
          const unitTypes = this.getUnitTypeRows(row).map((b) => ({
            ...b,
            label: `${b.label.split('/')[0]}BR / ${b.label.split('/')[1]}BA`,
            floorplans: this.getFloorplanRows(b)
          }))

          const result = [row]

          unitTypes.forEach((b) => {
            result.push(b)

            b.floorplans.forEach((floorplan) => {
              result.push({
                ...floorplan,
                parentLabel: b.label
              })
            })
          })

          return result
        })
        .map((row) => {
          const isUnitType = row.label.includes('BR / ')
          const isFloorplan = row.parentLabel !== undefined
          const isBuilding = !isUnitType && !isFloorplan

          return {
            Building: isBuilding ? row.label : '',
            'Unit Type': isUnitType ? row.label : '',
            'Floorplan Name': isFloorplan ? row.label : '',
            '# Leased': row.number_leased?.value || '',
            '# Active': row.currently_available?.value || '',
            'Days on Mkt': row.days_on_market?.value ? Math.round(row.days_on_market.value) : '',
            'Min SF': row.min_sqft?.value ? Math.round(row.min_sqft.value) : '',
            'Avg SF': row.sqft?.value ? Math.round(row.sqft.value) : '',
            'Max SF': row.max_sqft?.value ? Math.round(row.max_sqft.value) : '',
            'Min Rent': row.min_market_rent?.value ? Math.round(row.min_market_rent.value) : '',
            'Avg Rent': row.market_rent?.value ? Math.round(row.market_rent.value) : '',
            'Max Rent': row.max_market_rent?.value ? Math.round(row.max_market_rent.value) : '',
            'Rent PSF': exportDecimal(row.rent_psf?.value),
            NER: row.effective_rent?.value ? Math.round(row.effective_rent.value) : '',
            'Effective PSF': exportDecimal(row.effective_rent_psf?.value),
            'Concession %': exportDecimal(
              row.concession_percent?.value ? (row.concession_percent.value as number) * 100 : null
            )
          }
        })
      const today = new Date().toISOString().split('T')[0]

      exportFile(
        'hello-data-unit-type-table-' + today + '.csv',
        'Building,Unit Type,Floorplan Name,# Leased,# Active,Days on Mkt,Min SF,Avg SF,Max SF,Min Rent,Avg Rent,Max Rent,Rent PSF,NER,Effective PSF,Concession %\n' +
          data.map((row) => Object.values(row).join(',')).join('\n')
      )
      this.$tracker.exported('unit_type_details')
    },
    getPropertyRow(
      prop: PropertyDetailsResponse,
      label: string,
      units: UnitData[]
    ): {
      effective_rent_psf: ColumnCellData
      rent_psf: ColumnCellData
      currently_available: ColumnCellData
      trend: { value: number | null; date: string }[]
      label: string
      concession_percent: ColumnCellData
      min_sqft: ColumnCellData
      max_market_rent: ColumnCellData
      min_market_rent: ColumnCellData
      days_on_market: ColumnCellData
      effective_rent: ColumnCellData
      max_sqft: ColumnCellData
      number_leased: ColumnCellData
      sqft: ColumnCellData
      year_built: ColumnCellData
      market_rent: ColumnCellData
    } {
      const minDateStr = format(this.dateRange.from, 'yyyy-MM-dd')
      const maxDateStr = format(this.dateRange.to, 'yyyy-MM-dd')
      const newMaxDate = this.getMaxDate(prop)
      const newMaxDateStr = format(newMaxDate, 'yyyy-MM-dd')

      return {
        label,
        number_leased: this.getUnitAttr(
          units,
          ({ period }) => [period.exit_market && period.exit_market <= newMaxDateStr],
          (x) => x.length
        ),
        currently_available: this.getUnitAttr(
          units,
          ({ lastHistory, period }) => {
            return [lastHistory.to_date >= newMaxDateStr && period.exit_market !== newMaxDateStr]
          },
          (x) => x.length
        ),
        concession_percent: this.getUnitAttr(
          units,
          ({ lastHistory }) => {
            if (lastHistory.price && lastHistory.effective_price) {
              return (lastHistory.price - lastHistory.effective_price) / lastHistory.price
            } else if (
              lastHistory.min_price &&
              lastHistory.min_effective_price &&
              lastHistory.max_price &&
              lastHistory.max_effective_price
            ) {
              return (
                ((lastHistory.min_price - lastHistory.min_effective_price) / lastHistory.min_price +
                  (lastHistory.max_price - lastHistory.max_effective_price) /
                    lastHistory.max_price) /
                2.0
              )
            }
            return null
          },
          avg
        ),
        days_on_market: this.getUnitAttr(
          units,
          ({ period }) => {
            return daysOnMarket(period, this.dateRange.to)
          },
          avg
        ),
        min_market_rent: this.getUnitAttr(
          units,
          ({ lastHistory }) => lastHistory.price ?? lastHistory.min_price,
          safeMin
        ),
        market_rent: this.getUnitAttr(
          units,
          ({ lastHistory }) => lastHistory.price ?? lastHistory.min_price,
          avg
        ),
        max_market_rent: this.getUnitAttr(
          units,
          ({ lastHistory }) => lastHistory.price ?? lastHistory.max_price ?? lastHistory.min_price,
          safeMax
        ),
        effective_rent: this.getUnitAttr(
          units,
          ({ lastHistory }) => lastHistory.effective_price ?? lastHistory.min_effective_price,
          avg
        ),
        min_sqft: this.getUnitAttr(
          units,
          ({ unit }) => [unit.min_sqft, unit.sqft, unit.max_sqft],
          safeMin
        ),
        sqft: this.getUnitAttr(units, ({ unit }) => [unit.min_sqft, unit.sqft, unit.max_sqft], avg),
        max_sqft: this.getUnitAttr(
          units,
          ({ unit }) => [unit.min_sqft, unit.sqft, unit.max_sqft],
          safeMax
        ),
        rent_psf: this.getUnitAttr(
          units,
          ({ unit, lastHistory }) => [
            {
              sqft: unit.sqft || unit.min_sqft,
              price: lastHistory.price ?? lastHistory.min_price
            }
          ],
          avgPsf
        ),
        effective_rent_psf: this.getUnitAttr(
          units,
          ({ unit, lastHistory }) => [
            {
              sqft: unit.sqft || unit.min_sqft,
              price: lastHistory.effective_price ?? lastHistory.min_effective_price
            }
          ],
          avgPsf
        ),
        year_built: {
          value: prop.year_built,
          units: []
        },
        trend: getTrend(units, this.expandedUnitHistory, minDateStr, maxDateStr, this.metric.value)
      }
    },
    toggleBed(bedRow: { label: string; id: string }, parentRow: { label: string; id: string }) {
      const bedId = bedRow.id
      const parentId = parentRow.id
      if (this.expandedBedRows.get(bedId) === parentId) {
        this.expandedBedRows.delete(bedId)
      } else {
        this.expandedBedRows.set(bedId, parentId)
        this.$tracker.expandedRow(`${parentRow.label}-${bedRow.label}`, 'unit_types')
      }
    },
    sortingChanged(colName: string) {
      this.$tracker.changedSort('unit_types', colName)
    },
    sortColumn(a: { value: number | null }, b: { value: number | null }) {
      return (a.value || 0) - (b.value || 0)
    },
    togglePropertyRow(props: { row: { id: string }; expand: boolean }) {
      props.expand = !props.expand
      if (!props.expand) {
        // Clear only this parent's expanded bed rows when it collapses
        for (const [bedId, parentId] of this.expandedBedRows.entries()) {
          if (parentId === props.row.id) {
            this.expandedBedRows.delete(bedId)
          }
        }
      }
    },
    getColumnFormattedValue(columnField: Column, row: { [key: string]: number | string | null }) {
      const column = this.columns.find((_) => _.field === columnField)
      if (!column || !row[columnField]) {
        return ''
      }
      return column.format(row[columnField])
    },
    getMaxDate(prop: PropertyDetailsResponse) {
      return this.dateRange.to
        ? min([parseISO(prop.created_on), this.dateRange.to])
        : parseISO(prop.created_on)
    },
    openUnitPopup(
      label: string,
      prop: PropertyDetailsResponse,
      field: {
        value: string | number | null
        formattedValue?: string
        unit?: string
      },
      units: UnitData[]
    ) {
      if (units.length) {
        this.$tracker.openedPopup(label, 'unit_types')
        this.displayUnitPopup = true
        this.clickedCell = {
          label,
          prop,
          field,
          units
        }
      }
    },
    getUnitTypeRows(propRow: {
      prop: PropertyDetailsResponse
      units: {
        unit: Availability
        lastHistory: Price
        period: AvailabilityPeriod
      }[]
    }) {
      return Object.values(
        propRow.units.reduce(
          (acc, obj) => {
            const keyValue = `${obj.unit.bed}-${obj.unit.bath}-${obj.unit.partial_bath || '0'}`
            if (!acc[keyValue]) {
              acc[keyValue] = []
            }
            acc[keyValue].push(obj)
            return acc
          },
          {} as { [key: string]: UnitData[] }
        )
      )
        .filter((group: UnitData[]) => group.length > 0)
        .sort((a: UnitData[], b: UnitData[]) => {
          const aUnit = a[0].unit
          const bUnit = b[0].unit
          return (
            (aUnit.bed || -1) - (bUnit.bed || -1) ||
            (aUnit.bath || -1) - (bUnit.bath || -1) ||
            (aUnit.partial_bath || -1) - (bUnit.partial_bath || -1)
          )
        })
        .map((vals: UnitData[]) => ({
          id: `${propRow.prop.id}-${vals[0].unit.bed}-${vals[0].unit.bath}${vals[0].unit.partial_bath ? '.5' : ''}`,
          prop: propRow.prop,
          units: vals,
          ...this.getPropertyRow(
            propRow.prop,
            `${vals[0].unit.bed}/${vals[0].unit.bath}${vals[0].unit.partial_bath ? '.5' : ''}`,
            vals
          )
        }))
    },
    getFloorplanRows(unitTypeRow: {
      prop: PropertyDetailsResponse
      units: {
        unit: Availability
        lastHistory: Price
        period: AvailabilityPeriod
      }[]
    }) {
      // Group units by floorplan name
      const floorplanGroups = unitTypeRow.units.reduce(
        (acc, unit) => {
          const name =
            unit.unit.floorplan_name ||
            `${unit.unit.bed}BR / ${unit.unit.bath}${unit.unit.partial_bath ? '.5' : ''}BA`
          if (!acc[name]) {
            acc[name] = []
          }
          acc[name].push(unit)
          return acc
        },
        {} as { [key: string]: UnitData[] }
      )
      return Object.entries(floorplanGroups).map(([name, units]) => ({
        name,
        prop: unitTypeRow.prop,
        units,
        ...this.getPropertyRow(unitTypeRow.prop, name, units)
      }))
    }
  }
}
</script>

<style scoped lang="scss">
.title-row td {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.no-space {
  width: 1px;
  white-space: nowrap;
}
.floorplan-line {
  position: absolute;
  left: 36px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 16px;
}
</style>
